import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button, IconButton,
  Avatar, Divider, Tooltip, Snackbar, Alert
} from '@mui/material';
import { Phone, Add, Close, Star, ContentCopy } from '@mui/icons-material';

const STORAGE_KEY = 'ce_quickDial';

interface FavEntry { name: string; number: string; }

const loadFavs = (): FavEntry[] => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
};
const saveFavs = (list: FavEntry[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const initials = (name: string, number: string) =>
  (name || number || '?').charAt(0).toUpperCase();

const AVATAR_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];
const avatarColor = (s: string) => AVATAR_COLORS[(s.charCodeAt(0) || 0) % AVATAR_COLORS.length];

export const QuickDial: React.FC = () => {
  const [favs, setFavs]       = useState<FavEntry[]>(loadFavs);
  const [showForm, setShowForm] = useState(false);
  const [name, setName]        = useState('');
  const [number, setNumber]    = useState('');
  const [numErr, setNumErr]    = useState(false);
  const [snack, setSnack]      = useState('');

  useEffect(() => { saveFavs(favs); }, [favs]);

  const addFav = useCallback(() => {
    if (!number.trim()) { setNumErr(true); return; }
    setFavs(prev => [...prev, { name: name.trim(), number: number.trim() }]);
    setName(''); setNumber(''); setShowForm(false); setNumErr(false);
  }, [name, number]);

  const removeFav = (idx: number) => setFavs(prev => prev.filter((_, i) => i !== idx));

  const dial = (num: string) => {
    window.location.href = `callessential://call/${encodeURIComponent(num)}`;
  };

  const copyNum = (num: string) => {
    navigator.clipboard.writeText(num).catch(() => {});
    setSnack('Number copied to clipboard');
  };

  return (
    <Card>
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ color: '#f59e0b', fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700}>Quick Dial</Typography>
          </Box>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => setShowForm(v => !v)}
            variant={showForm ? 'contained' : 'outlined'}
            sx={{ minWidth: 0 }}
          >
            Add
          </Button>
        </Box>

        {/* Add form */}
        {showForm && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small" label="Name (optional)" value={name} fullWidth
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addFav()}
              />
              <TextField
                size="small" label="Number *" value={number} fullWidth
                error={numErr} helperText={numErr ? 'Required' : ''}
                onChange={e => { setNumber(e.target.value); setNumErr(false); }}
                onKeyDown={e => e.key === 'Enter' && addFav()}
                type="tel"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={addFav} sx={{ flex: 1 }}>Save</Button>
              <Button size="small" variant="outlined" onClick={() => { setShowForm(false); setName(''); setNumber(''); setNumErr(false); }}>Cancel</Button>
            </Box>
          </Box>
        )}

        {/* List */}
        {favs.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Star sx={{ fontSize: 36, opacity: 0.25, mb: 1 }} />
            <Typography variant="body2" fontWeight={600} gutterBottom>No quick dial numbers yet</Typography>
            <Typography variant="caption">Add frequently called numbers for one-tap dialling</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {favs.map((fav, idx) => (
              <Box key={idx} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1, borderRadius: 2, '&:hover': { bgcolor: 'grey.50' },
                transition: 'background .15s',
              }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: avatarColor(fav.name || fav.number), fontSize: 14, fontWeight: 700 }}>
                  {initials(fav.name, fav.number)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>{fav.name || fav.number}</Typography>
                  {fav.name && <Typography variant="caption" color="text.secondary" noWrap>{fav.number}</Typography>}
                </Box>
                <Tooltip title="Copy number">
                  <IconButton size="small" onClick={() => copyNum(fav.number)} sx={{ color: 'text.secondary' }}>
                    <ContentCopy sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in CallEssential Phone">
                  <IconButton size="small" onClick={() => dial(fav.number)}
                    sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' }, width: 30, height: 30 }}>
                    <Phone sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeFav(idx)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                    <Close sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <Snackbar open={!!snack} autoHideDuration={2000} onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnack('')} sx={{ width: '100%' }}>{snack}</Alert>
      </Snackbar>
    </Card>
  );
};
